import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, ExternalLink, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import CodePanel from '@stepflow/ui-shared/components/CodePanel.jsx';
import MarkdownRenderer from '@stepflow/ui-shared/components/MarkdownRenderer.jsx';
import { getDiscussionFileText } from '@stepflow/ui-shared/services/dataPlaneApi';
import { getAgentWorkspaceRawUrl, getAgentWorkspaceFile } from '@stepflow/ui-shared/services/toolsApi';
import { roomFileUrl, splitName, formatBytes, fileKind } from '../../utils/roomFileUrls.js';
import { codeLangOf } from '@stepflow/ui-shared/utils/codeLang.js';

// the multi-format viewer (pdf.js + office renderers) loads ONLY when a document is opened — never in the base bundle
const LazyDocView = lazy(() => import('@stepflow/ui-shared/components/workspace/WorkspaceDocView.jsx'));

const OFFICE = new Set(['doc', 'sheet', 'slides']);

/** Which tab opens first: Office → TEXT (the original viewer hands Office files to an external online viewer — the
 *  Text view keeps the content in StepFlow); everything else → the ORIGINAL. Pure. */
export function defaultDocTab(att) {
  // every type previews IN StepFlow now (Office included — ``OfficePreview``), so everything opens on Preview
  return 'original';
}

/** OFFICE PREVIEW sections from the file's extracted Markdown (``doc_markdown``: ``## Slide N — title`` per slide,
 *  ``## Sheet: name`` per sheet, else one document). The provenance header is dropped. Pure.
 *  → {kind: 'slides'|'sheets'|'doc', items: [{key, title, n?, body}]} */
export function officeSections(text = '', kind = '') {
  const body = String(text || '').replace(/^# .*\n+(> Extracted from .*\n+)?/, '');
  const split = (re, mk) => {
    const out = [];
    const marks = [...body.matchAll(re)];
    marks.forEach((m, i) => {
      const end = i + 1 < marks.length ? marks[i + 1].index : body.length;
      out.push(mk(m, body.slice(m.index + m[0].length, end).trim()));
    });
    return out;
  };
  if (kind === 'slides') {
    const items = split(/^## Slide (\d+)(?: — (.*))?$/gm, (m, b) => ({ key: `s${m[1]}`, n: Number(m[1]), title: (m[2] || '').trim(), body: b }));
    if (items.length) return { kind: 'slides', items };
  }
  if (kind === 'sheet') {
    const items = split(/^## Sheet: (.*)$/gm, (m, b) => ({ key: `t${m[1]}`, title: m[1].trim(), body: b }));
    if (items.length) return { kind: 'sheets', items };
  }
  return { kind: 'doc', items: [{ key: 'doc', title: '', body: body.trim() }] };
}

/** OFFICE PREVIEW — DOCX / XLSX / PPTX rendered INSIDE StepFlow from the text we read (slides as slide cards, sheets as
 *  tabs, a document as a page). It replaces the external online viewer, which needed the file sent to a third party and
 *  failed on our short-lived links ("404 - File or directory not found"). Layout + images aren't reproduced — said so,
 *  with Download for the original. */
function OfficePreview({ networkId, poolId, message, att, onDownload }) {
  const kind = fileKind(att.name, att.type);
  const status = att.workspace ? 'ready' : ((att.extract && att.extract.status) || '');
  const [st, setSt] = useState({ loading: true, text: '', error: '' });
  const [sheet, setSheet] = useState(0);
  useEffect(() => {
    let gone = false;
    if (status === 'reading') { setSt({ loading: false, text: '', error: '' }); return undefined; }
    setSt({ loading: true, text: '', error: '' });
    Promise.resolve()
      .then(() => (att.workspace
        ? getAgentWorkspaceFile(networkId, '_room_', att.workspace.path, { workspaceId: att.workspace.workspaceId })
          .then((f) => ({ status: f && f.text != null ? 'ready' : 'failed', text: (f && f.text) || '' }))
        : getDiscussionFileText(networkId, message, att.index, { pool: poolId })))
      .then((d) => { if (!gone) setSt({ loading: false, text: (d && d.status === 'ready' && d.text) || '', error: (d && d.error) || '' }); })
      .catch((e) => { if (!gone) setSt({ loading: false, text: '', error: (e && e.message) || "Couldn't load it." }); });
    return () => { gone = true; };
  }, [networkId, poolId, message, att.index, status, att.workspace && att.workspace.path]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (status === 'reading') {
    return <div data-testid="doc-office-reading" className="flex justify-center py-12 text-[0.875rem] text-gray-500"><Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden /> Reading the document…</div>;
  }
  if (st.loading) return <div className="flex justify-center py-12 text-[0.875rem] text-gray-400"><Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden /> Loading…</div>;
  if (!st.text) {
    return (
      <div data-testid="doc-office-empty" className="px-4 py-12 text-center text-[0.875rem] text-gray-600">
        {st.error || "There's no preview of this file yet."}
        {onDownload ? <> <button type="button" onClick={onDownload} className="font-medium text-blue-700 hover:underline">Download it</button> to open it in its app.</> : null}
      </div>
    );
  }
  const sec = officeSections(st.text, kind);
  const note = (
    <div data-testid="doc-office-note" className="mx-auto mb-4 max-w-4xl text-[0.75rem] text-gray-500">
      A preview of the file&apos;s content, inside StepFlow — layout and images aren&apos;t shown.{' '}
      {onDownload ? <button type="button" onClick={onDownload} className="font-medium text-blue-700 hover:underline">Download the original</button> : null}
    </div>
  );
  if (sec.kind === 'slides') {
    return (
      <div data-testid="doc-office-slides" className="px-6 py-6">
        {note}
        <div className="mx-auto flex max-w-4xl flex-col gap-5">
          {sec.items.map((sl) => (
            <section key={sl.key} data-testid={`doc-slide-${sl.n}`}
              className="relative flex aspect-[16/9] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white px-10 py-8">
              <span className="absolute right-3 top-2 text-[0.75rem] tabular-nums text-gray-400">{sl.n}</span>
              {sl.title ? <h2 className="mb-3 text-[1.5rem] font-bold leading-tight text-gray-900">{sl.title}</h2> : null}
              <div className="min-h-0 flex-1 overflow-auto text-[1rem]"><MarkdownRenderer content={sl.body || '_(no text on this slide)_'} /></div>
            </section>
          ))}
        </div>
      </div>
    );
  }
  if (sec.kind === 'sheets') {
    const cur = sec.items[Math.min(sheet, sec.items.length - 1)];
    return (
      <div data-testid="doc-office-sheets" className="px-6 py-6">
        {note}
        <div className="mx-auto max-w-6xl">
          {sec.items.length > 1 ? (
            <div role="tablist" className="mb-3 flex flex-wrap gap-1 border-b border-gray-200">
              {sec.items.map((t, i) => (
                <button key={t.key} type="button" role="tab" aria-selected={i === sheet} data-testid={`doc-sheet-tab-${i}`}
                  onClick={() => setSheet(i)}
                  className={`-mb-px border-b-2 px-3 py-1.5 text-[0.875rem] font-medium ${i === sheet ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
                  {t.title}
                </button>
              ))}
            </div>
          ) : null}
          <div className="overflow-auto rounded-lg border border-gray-200 bg-white p-4"><MarkdownRenderer content={cur.body} /></div>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="doc-office-doc" className="px-6 py-6">
      {note}
      <article className="mx-auto max-w-3xl rounded-lg border border-gray-200 bg-white px-12 py-10 text-[1rem]">
        <MarkdownRenderer content={sec.items[0].body} />
      </article>
    </div>
  );
}

/** ``att.workspace = {path, workspaceId}`` → a file in the room WORKSPACE (a Do output, CONTEXT-DOCS §H1), not a
 *  shared attachment: its URL + text come from the workspace browser. Pure. */
const _wsUrl = (networkId, att) => getAgentWorkspaceRawUrl(networkId, '_room_', att.workspace.path, { workspaceId: att.workspace.workspaceId });

function TextView({ networkId, poolId, message, att }) {
  const status = att.workspace ? 'ready' : ((att.extract && att.extract.status) || '');
  const [state, setState] = useState({ loading: true, data: null, error: '' });
  useEffect(() => {
    let gone = false;
    if (status === 'reading') { setState({ loading: false, data: null, error: '' }); return undefined; }
    setState({ loading: true, data: null, error: '' });
    Promise.resolve()
      .then(() => (att.workspace
        ? getAgentWorkspaceFile(networkId, '_room_', att.workspace.path, { workspaceId: att.workspace.workspaceId })
          .then((f) => ({ status: f && f.text != null ? 'ready' : 'failed', text: (f && f.text) || '',
            error: f && f.binary ? 'No text view for this file — open the Preview.' : '' }))
        : getDiscussionFileText(networkId, message, att.index, { pool: poolId })))
      .then((d) => { if (!gone) setState({ loading: false, data: d, error: '' }); })
      .catch((e) => { if (!gone) setState({ loading: false, data: null, error: (e && e.message) || "Couldn't load the text." }); });
    return () => { gone = true; };
  }, [networkId, poolId, message, att.index, status, att.workspace && att.workspace.path]);
  if (status === 'reading') {
    return <div data-testid="doc-text-reading" className="flex justify-center py-8 text-[0.8125rem] text-gray-500"><Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden /> Reading the document…</div>;
  }
  if (state.loading) return <div className="flex justify-center py-8 text-[0.8125rem] text-gray-400"><Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden /> Loading…</div>;
  const d = state.data || {};
  if (state.error || d.status === 'failed' || !d.text) {
    return <div data-testid="doc-text-error" className="px-4 py-8 text-center text-[0.8125rem] text-rose-600">{state.error || d.error || "There's no text from this document yet."}</div>;
  }
  return (
    <div data-testid="doc-text" className="px-4 py-3">
      <div className="mb-2 text-[0.75rem] text-gray-500">
        This is exactly what the context reads{d.ocrPages ? ` · ${d.ocrPages} page${d.ocrPages === 1 ? '' : 's'} read with OCR` : ''}{d.truncated ? ' · cut to the size limit' : ''}.
      </div>
      <MarkdownRenderer content={d.text} size="chat" />
    </div>
  );
}

/** PREVIEW of a text-like file (Markdown / text / code): the FILE ITSELF, fetched — the multi-format viewer has no
 *  renderer for these (it drew nothing for a .md). Markdown renders, code is highlighted, text stays as typed. Files
 *  over ``PREVIEW_MAX`` show their start (stated). */
export const PREVIEW_MAX = 2 * 1024 * 1024;
function TextFilePreview({ url, att }) {
  const [st, setSt] = useState({ loading: true, text: '', cut: false, error: '' });
  useEffect(() => {
    let gone = false;
    setSt({ loading: true, text: '', cut: false, error: '' });
    const big = Number(att.size || 0) > PREVIEW_MAX;
    fetch(url, big ? { headers: { Range: `bytes=0-${PREVIEW_MAX - 1}` } } : undefined)
      .then((r) => { if (!r.ok && r.status !== 206) throw new Error(`HTTP ${r.status}`); return r.text(); })
      .then((t) => { if (!gone) setSt({ loading: false, text: t, cut: big, error: '' }); })
      .catch((e) => { if (!gone) setSt({ loading: false, text: '', cut: false, error: (e && e.message) || "Couldn't load it." }); });
    return () => { gone = true; };
  }, [url, att.size]);
  if (st.loading) return <div className="flex justify-center py-10 text-[0.8125rem] text-gray-400"><Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden /> Loading…</div>;
  if (st.error) return <div data-testid="doc-preview-error" className="px-4 py-10 text-center text-[0.8125rem] text-rose-600">{st.error}</div>;
  const kind = fileKind(att.name, att.type);
  const lang = codeLangOf(att.name);
  const note = st.cut ? <div className="mb-3 text-[0.75rem] text-gray-500">Showing the first {formatBytes(PREVIEW_MAX)} — download it for the rest.</div> : null;
  if (/\.(md|markdown)$/i.test(att.name || '')) {
    return <div data-testid="doc-preview-md" className="mx-auto max-w-3xl px-8 py-6">{note}<MarkdownRenderer content={st.text} /></div>;
  }
  if (lang || kind === 'code') {
    return (
      <div data-testid="doc-preview-code" className="px-6 py-5">{note}
        <CodePanel code={st.text} grammar={(lang && lang.grammar) || ''} title={att.name} previewLines={100000} />
      </div>
    );
  }
  return <pre data-testid="doc-preview-text" className="mx-auto max-w-4xl whitespace-pre-wrap break-words px-8 py-6 font-mono text-[0.8125rem] leading-relaxed text-gray-800">{note}{st.text}</pre>;
}

/**
 * THE FILE VIEWER (CONTEXT-DOCS.md §P1/§P2; Slack-style, 2026-10-14) — a shared document opens in a near-full-screen
 * dialog over the room (was a 28 rem side pane: documents didn't fit and got squeezed). Header: title, type · size ·
 * who shared it, ‹ n of N › when the message carries several files (← / → keys), Download, Open in a new tab, ✕ (Esc).
 * Tabs: PREVIEW — the file itself (pdf / image / office viewer, and Markdown / text / code rendered here) — and
 * WHAT AGENTS READ — exactly the text the context uses (``TextView``). Office opens on the latter (``defaultDocTab``).
 */
export default function DocPane({ networkId, poolId, message, att: initialAtt, onClose }) {
  const siblings = useMemo(() => (!initialAtt.workspace && Array.isArray(message.attachments) && message.attachments.length > 1
    ? message.attachments : []), [message, initialAtt.workspace]);
  const [att, setAtt] = useState(initialAtt);
  useEffect(() => { setAtt(initialAtt); }, [initialAtt]);
  const pos = siblings.findIndex((a) => a.index === att.index);
  const step = useCallback((d) => {
    if (!siblings.length || pos < 0) return;
    setAtt(siblings[(pos + d + siblings.length) % siblings.length]);
  }, [siblings, pos]);
  const [tab, setTab] = useState(() => defaultDocTab(att));
  const [state, setState] = useState({ loading: true, url: '', error: '' });
  useEffect(() => { setTab(defaultDocTab(att)); }, [message.id, att.index, att.workspace && att.workspace.path]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let gone = false;
    setState({ loading: true, url: '', error: '' });
    (att.workspace ? Promise.resolve().then(() => _wsUrl(networkId, att)) : roomFileUrl(networkId, poolId, message, att.index))
      .then((r) => { if (!gone) setState({ loading: false, url: (r && r.url) || '', error: r && r.url ? '' : "Couldn't open it." }); })
      .catch((e) => { if (!gone) setState({ loading: false, url: '', error: (e && e.message) || "Couldn't open it." }); });
    return () => { gone = true; };
  }, [networkId, poolId, message.id, att.index, att.workspace && att.workspace.path]); // eslint-disable-line react-hooks/exhaustive-deps
  const closeRef = useRef(null);
  useEffect(() => {
    const prev = document.activeElement;
    const body = document.body;
    const overflow = body.style.overflow;
    body.style.overflow = 'hidden';                                 // the room doesn't scroll behind the viewer
    if (closeRef.current) closeRef.current.focus();
    return () => { body.style.overflow = overflow; if (prev && typeof prev.focus === 'function') prev.focus(); };
  }, []);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' && siblings.length) step(1);
      else if (e.key === 'ArrowLeft' && siblings.length) step(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, step, siblings.length]);
  const { title, ext } = splitName(att.name);
  const kind = fileKind(att.name, att.type);
  const textLike = kind === 'text' || kind === 'code' || !!codeLangOf(att.name);
  const iconBtn = 'inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900';
  const tabBtn = (key, label, hint) => (
    <button type="button" role="tab" aria-selected={tab === key} data-testid={`doc-tab-${key}`} onClick={() => setTab(key)}
      title={hint}
      className={`rounded-md px-3 py-1 text-[0.8125rem] font-medium transition-colors ${tab === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
      {label}
    </button>
  );
  const download = () => {
    if (!state.url) return;
    const a = document.createElement('a');
    a.href = state.url; a.download = att.name; a.rel = 'noopener';
    document.body.appendChild(a); a.click(); a.remove();
  };
  const dialog = (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-gray-900/70 p-3 backdrop-blur-[2px] md:p-8"
      data-testid="doc-viewer-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div data-testid="doc-pane" role="dialog" aria-modal="true" aria-label={att.name}
        className="sf-rise flex h-full w-full max-w-[1400px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex h-14 shrink-0 items-center gap-3 border-b border-gray-200 px-4">
          <div className="min-w-0 flex-1">
            <div className="truncate text-[0.9375rem] font-bold text-gray-900" title={att.name}>{title}</div>
            <div className="truncate text-[0.75rem] text-gray-500">{[ext, formatBytes(att.size), att.workspace ? `made by ${message.agentName || 'an agent'}` : message.authorName].filter(Boolean).join(' · ')}</div>
          </div>
          <div role="tablist" className="flex gap-0.5 rounded-lg bg-gray-100 p-0.5">
            {tabBtn('original', 'Preview', 'The file itself')}
            {tabBtn('text', 'What agents read', 'Exactly the text the room\u2019s context and agents read from this file')}
          </div>
          {siblings.length ? (
            <div className="flex items-center gap-0.5" data-testid="doc-nav">
              <button type="button" aria-label="Previous file" title="Previous (←)" data-testid="doc-prev" onClick={() => step(-1)} className={iconBtn}>
                <ChevronLeft className="h-5 w-5" aria-hidden />
              </button>
              <span className="min-w-[3.5rem] text-center text-[0.75rem] tabular-nums text-gray-500" data-testid="doc-pos">{pos + 1} of {siblings.length}</span>
              <button type="button" aria-label="Next file" title="Next (→)" data-testid="doc-next" onClick={() => step(1)} className={iconBtn}>
                <ChevronRight className="h-5 w-5" aria-hidden />
              </button>
            </div>
          ) : null}
          {state.url ? (
            <>
              <button type="button" data-testid="doc-pane-download" aria-label="Download" title="Download" onClick={download} className={iconBtn}>
                <Download className="h-[1.125rem] w-[1.125rem]" aria-hidden />
              </button>
              <a href={state.url} target="_blank" rel="noopener noreferrer" data-testid="doc-pane-open" aria-label="Open in a new tab"
                title="Open in a new tab" className={iconBtn}>
                <ExternalLink className="h-[1.125rem] w-[1.125rem]" aria-hidden />
              </a>
            </>
          ) : null}
          <button ref={closeRef} type="button" data-testid="doc-pane-close" aria-label="Close" title="Close (Esc)" onClick={onClose} className={iconBtn}>
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto bg-gray-50" data-testid="doc-pane-body">
          {tab === 'text' ? (
            <div className="mx-auto max-w-4xl bg-white"><TextView networkId={networkId} poolId={poolId} message={message} att={att} /></div>
          ) : state.loading ? (
            <div className="flex justify-center py-10 text-[0.8125rem] text-gray-400"><Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden /> Opening…</div>
          ) : state.error ? (
            <div data-testid="doc-pane-error" className="px-4 py-10 text-center text-[0.8125rem] text-rose-600">{state.error}</div>
          ) : kind === 'image' ? (
            <div className="flex h-full items-center justify-center p-4">
              <img data-testid="doc-preview-image" src={state.url} alt={att.name} className="max-h-full max-w-full object-contain" />
            </div>
          ) : textLike ? (
            <div className="min-h-full bg-white"><TextFilePreview url={state.url} att={att} /></div>
          ) : OFFICE.has(kind) ? (
            <OfficePreview networkId={networkId} poolId={poolId} message={message} att={att} onDownload={download} />
          ) : (
            <Suspense fallback={<div className="flex justify-center py-10 text-[0.8125rem] text-gray-400"><Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden /> Loading the viewer…</div>}>
              <div className="h-full bg-white"><LazyDocView url={state.url} fileName={att.name} /></div>
            </Suspense>
          )}
        </div>
      </div>
    </div>
  );
  return typeof document !== 'undefined' ? createPortal(dialog, document.body) : dialog;
}
